/**
 * Cloudflare Turnstile utility for GonkaGame configured domain domain.
 * Mirrors the API of recaptcha.ts for consistent usage.
 *
 * HK-IGRA.940: на igra.now Turnstile работает НЕВИДИМО — первой попыткой перед
 * видимой GeeTest (getInvisibleTurnstileToken ниже). Ключ по умолчанию —
 * виджет «igra.now» в Cloudflare (режим invisible, домены igra.now и
 * localhost). Ключ сайта публичный, секрет живёт только на бэкенде
 * (IGRA_TURNSTILE_SECRET).
 */

const DEFAULT_TURNSTILE_SITE_KEY = '0x4AAAAAAFHt2pi05cGcOoXn'
const TURNSTILE_SITE_KEY =
    import.meta.env.VITE_TURNSTILE_SITE_KEY ?? DEFAULT_TURNSTILE_SITE_KEY

declare global {
    interface Window {
        turnstile?: {
            render(
                container: HTMLElement | string,
                parameters: {
                    sitekey: string
                    theme?: 'light' | 'dark' | 'auto'
                    callback?: (token: string) => void
                    'error-callback'?: () => void
                    'expired-callback'?: () => void
                    'unsupported-callback'?: () => void
                    execution?: 'render' | 'execute'
                    retry?: 'auto' | 'never'
                }
            ): string
            reset(widgetId?: string): void
            execute(widgetId?: string): void
            getResponse(widgetId?: string): string
            remove(widgetId?: string): void
        }
    }
}

let loadPromise: Promise<void> | null = null

const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export const getTurnstileSiteKey = (): string => TURNSTILE_SITE_KEY

/**
 * Load Cloudflare Turnstile script.
 * The script is loaded once and cached.
 */
export const loadTurnstile = (): Promise<void> => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Turnstile недоступен в SSR среде'))
    }

    if (window.turnstile?.render) {
        return Promise.resolve()
    }

    if (loadPromise) {
        return loadPromise
    }

    loadPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src^="https://challenges.cloudflare.com/turnstile"]`
        )

        if (existingScript) {
            const checkReady = () => {
                if (window.turnstile?.render) {
                    resolve()
                } else {
                    setTimeout(checkReady, 50)
                }
            }
            checkReady()
            return
        }

        const script = document.createElement('script')
        script.src = TURNSTILE_SRC
        script.async = true
        script.defer = true
        script.onload = () => {
            const checkReady = () => {
                if (window.turnstile?.render) {
                    resolve()
                } else {
                    setTimeout(checkReady, 50)
                }
            }
            checkReady()
        }
        script.onerror = (e) => {
            console.error('[GonkaGame] Failed to load Turnstile script', e)
            script.remove()
            loadPromise = null
            reject(new Error('Не удалось загрузить Cloudflare Turnstile'))
        }
        document.head.appendChild(script)
    })

    return loadPromise
}

/**
 * Render a Turnstile widget in the given container.
 * Returns the widget ID for later operations.
 */
export const renderTurnstileWidget = async (
    containerId: string,
    callbacks: {
        onSuccess?: (token: string) => void
        onError?: () => void
        onExpired?: () => void
    } = {}
): Promise<string> => {
    await loadTurnstile()

    if (!window.turnstile) {
        throw new Error('Cloudflare Turnstile недоступен')
    }

    const widgetId = window.turnstile.render(`#${containerId}`, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: 'light',
        execution: 'execute', // Manual execution to prevent auto-submission on load
        callback: callbacks.onSuccess,
        'error-callback': callbacks.onError,
        'expired-callback': callbacks.onExpired,
    })

    return widgetId
}

/**
 * Manually trigger Turnstile verification (used when execution is 'execute').
 */
export const executeTurnstileWidget = (widgetId: string): void => {
    if (window.turnstile) {
        window.turnstile.execute(widgetId)
    }
}

/**
 * Reset a Turnstile widget to allow re-verification.
 */
export const resetTurnstileWidget = (widgetId: string): void => {
    if (window.turnstile) {
        try {
            window.turnstile.reset(widgetId)
        } catch {
            // Ignore reset errors
        }
    }
}

/**
 * Get the current response token from a widget.
 */
export const getTurnstileResponse = (widgetId: string): string => {
    if (window.turnstile) {
        return window.turnstile.getResponse(widgetId) || ''
    }
    return ''
}

/**
 * Remove a widget from the page.
 */
export const removeTurnstileWidget = (widgetId: string): void => {
    if (window.turnstile) {
        try {
            window.turnstile.remove(widgetId)
        } catch {
            // Ignore remove errors
        }
    }
}

/** Сколько ждём невидимую проверку, прежде чем показать GeeTest. */
export const INVISIBLE_TURNSTILE_TIMEOUT_MS = 6000

/** Токены, выданные невидимой проверкой: по ним выбирается поле запроса. */
const issuedTokens = new Set<string>()

/**
 * Невидимая больше не пробуется до перезагрузки страницы: она уже не
 * сработала (скрипт не загрузился, Cloudflare не пропустил, бэкенд отказал).
 * Повторять — значит каждый раз заставлять человека ждать перед GeeTest.
 */
let invisibleOff = false

export const isTurnstileToken = (token: string): boolean => issuedTokens.has(token)

export const requireVisibleCaptcha = (): void => {
    invisibleOff = true
}

/** Только для тестов: вернуть модуль в исходное состояние. */
export const __resetInvisibleTurnstile = (): void => {
    invisibleOff = false
    issuedTokens.clear()
}

/**
 * Токен невидимой Turnstile или `null`, если её не хватило — тогда вызывающий
 * показывает видимую GeeTest. Человек здесь ничего не видит: виджет режима
 * invisible рисуется в спрятанный за экраном контейнер и сразу убирается.
 */
export const getInvisibleTurnstileToken = async (
    timeoutMs: number = INVISIBLE_TURNSTILE_TIMEOUT_MS
): Promise<string | null> => {
    if (invisibleOff || typeof window === 'undefined' || !TURNSTILE_SITE_KEY) {
        return null
    }

    const container = document.createElement('div')
    container.setAttribute('aria-hidden', 'true')
    container.style.cssText =
        'position:fixed;left:-10000px;top:0;width:1px;height:1px;overflow:hidden'
    document.body.appendChild(container)

    let settled = false
    let widgetId: string | null = null

    const token = await new Promise<string | null>((resolve) => {
        const done = (value: string | null) => {
            if (settled) return
            settled = true
            clearTimeout(timer)
            resolve(value)
        }
        const timer = setTimeout(() => done(null), timeoutMs)

        loadTurnstile()
            .then(() => {
                if (settled || !window.turnstile) return done(null)
                widgetId = window.turnstile.render(container, {
                    sitekey: TURNSTILE_SITE_KEY,
                    retry: 'never',
                    callback: (value) => done(value || null),
                    'error-callback': () => done(null),
                    'expired-callback': () => done(null),
                    'unsupported-callback': () => done(null),
                })
            })
            .catch(() => done(null))
    })

    if (widgetId) removeTurnstileWidget(widgetId)
    container.remove()

    if (token === null) {
        invisibleOff = true
        return null
    }

    issuedTokens.add(token)
    return token
}
